import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Drawer,
  Stack
} from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const PLAN_COLORS = { trial: 'warning', basic: 'info', pro: 'success' };
const STATUS_COLORS = { active: 'success', expired: 'error', suspended: 'default' };

const daysLeft = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-IN') : '—');

const toIsoDateTime = (dateOnly) => (dateOnly ? `${dateOnly}T00:00:00` : null);

const toDateInputValue = (isoDate) => (isoDate ? new Date(isoDate).toISOString().slice(0, 10) : '');

const EMPTY_FORM = {
  plan_tier: 'trial',
  status: 'active',
  trial_ends_at: '',
  current_period_start: '',
  current_period_end: '',
  amount_paid: '',
  payment_date: '',
  payment_reference: '',
  notes: ''
};

const Subscriptions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [planFilter, setPlanFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, schoolId: null, schoolName: '' });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan_tier = planFilter;
      if (search) params.search = search;
      if (expiringSoon) params.expiring_soon = 'true';
      const response = await superAdminAPI.getSubscriptions(params);
      setSubscriptions(response.data.subscriptions);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load subscriptions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter, search, expiringSoon]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openEdit = (sub) => {
    setForm({
      plan_tier: sub.plan_tier,
      status: sub.status,
      trial_ends_at: toDateInputValue(sub.trial_ends_at),
      current_period_start: toDateInputValue(sub.current_period_start),
      current_period_end: toDateInputValue(sub.current_period_end),
      amount_paid: sub.amount_paid || '',
      payment_date: toDateInputValue(sub.payment_date),
      payment_reference: sub.payment_reference || '',
      notes: sub.notes || ''
    });
    setDrawer({ open: true, schoolId: sub.school_id, schoolName: sub.school_name });
  };

  const closeDrawer = () => setDrawer({ open: false, schoolId: null, schoolName: '' });

  const handleSave = async () => {
    setSaving(true);
    try {
      await superAdminAPI.updateSchoolSubscription(drawer.schoolId, {
        plan_tier: form.plan_tier,
        status: form.status,
        trial_ends_at: toIsoDateTime(form.trial_ends_at),
        current_period_start: toIsoDateTime(form.current_period_start),
        current_period_end: toIsoDateTime(form.current_period_end),
        amount_paid: form.amount_paid === '' ? null : form.amount_paid,
        payment_date: form.payment_date || null,
        payment_reference: form.payment_reference,
        notes: form.notes
      });
      toast.success('Subscription updated');
      closeDrawer();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update subscription');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const statCards = [
    { label: 'Trial', value: stats?.trial_count, status: 'active', plan: 'trial' },
    { label: 'Basic', value: stats?.basic_count, status: 'active', plan: 'basic' },
    { label: 'Pro', value: stats?.pro_count, status: 'active', plan: 'pro' },
    { label: 'Expired', value: stats?.expired_count, status: 'expired', plan: '' },
    { label: 'Expiring soon', value: stats?.expiring_soon_count, status: '', plan: '', expiring: true }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Subscriptions
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/superadmin/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={2.4} key={card.label}>
            <Card
              elevation={1}
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => {
                setExpiringSoon(!!card.expiring);
                setStatusFilter(card.expiring ? '' : card.status);
                setPlanFilter(card.expiring ? '' : card.plan);
              }}
            >
              <CardContent>
                <Typography color="text.secondary" fontSize="0.875rem">{card.label}</Typography>
                <Typography variant="h4" fontWeight="bold">{card.value ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }} size="small">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
          </TextField>
          <TextField select label="Plan" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} sx={{ minWidth: 160 }} size="small">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
            <MenuItem value="basic">Basic</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
          </TextField>
          <TextField
            label="Search school name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>School</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Period end</strong></TableCell>
                <TableCell><strong>Amount paid</strong></TableCell>
                <TableCell><strong>Days left</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>No subscriptions found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((sub) => {
                  const left = daysLeft(sub.current_period_end || sub.trial_ends_at);
                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell>{sub.school_name}</TableCell>
                      <TableCell>
                        <Chip label={sub.plan_tier} color={PLAN_COLORS[sub.plan_tier]} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={sub.status} color={STATUS_COLORS[sub.status]} size="small" />
                      </TableCell>
                      <TableCell>
                        {sub.current_period_end
                          ? formatDate(sub.current_period_end)
                          : sub.trial_ends_at
                            ? `Trial until ${formatDate(sub.trial_ends_at)}`
                            : '—'}
                      </TableCell>
                      <TableCell>
                        {sub.amount_paid ? `₹${Number(sub.amount_paid).toLocaleString('en-IN')}` : '—'}
                      </TableCell>
                      <TableCell>
                        {left === null ? '—' : left < 0 ? 'Expired' : left < 30 ? `${left} days ⚠` : `${left} days`}
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" startIcon={<Edit />} onClick={() => openEdit(sub)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Drawer anchor="right" open={drawer.open} onClose={saving ? undefined : closeDrawer}>
        <Box sx={{ width: { xs: 320, sm: 400 }, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {drawer.schoolName}
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField select label="Plan tier" value={form.plan_tier} onChange={(e) => setForm({ ...form, plan_tier: e.target.value })}>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="basic">Basic</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
            </TextField>
            <TextField select label="Subscription status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
            {form.plan_tier === 'trial' && (
              <TextField
                label="Trial ends"
                type="date"
                value={form.trial_ends_at}
                onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Period start"
              type="date"
              value={form.current_period_start}
              onChange={(e) => setForm({ ...form, current_period_start: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Period end"
              type="date"
              value={form.current_period_end}
              onChange={(e) => setForm({ ...form, current_period_end: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Amount paid (₹)"
              type="number"
              value={form.amount_paid}
              onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
            />
            <TextField
              label="Payment date"
              type="date"
              value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Payment reference"
              value={form.payment_reference}
              onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
              placeholder="e.g. RAZORPAY-123 or NEFT-REF"
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              multiline
              minRows={3}
              helperText="Internal notes, not visible to the school"
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={closeDrawer} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </Container>
  );
};

export default Subscriptions;
